<template>
    <main class="p-5 bg-echoes-background-black">
        <!-- Header Section -->
        <UsersHeader>
            <template #title>Tags Management</template>
            <template #description>
                Manage and organize your tags from a central dashboard.
            </template>
            <template #actions>
            <button 
                                @click="showImportModal = true" 
                                 class="inline-flex items-center px-4 py-2.5 text-base font-medium rounded-xl bg-echoes-background-black/60 text-white hover:bg-white/5 transition-colors duration-200 border border-echoes-border-light mr-3"
                            >
                                <i class="mdi mdi-file-upload mr-2"></i> Import Tags
                            </button>
            <button 
                                @click="createTag" 
                                 class="inline-flex items-center px-4 py-2.5 text-base font-medium rounded-xl bg-echoes-primary text-white hover:bg-echoes-primary/80 transition-colors duration-200"
                            >
                                <i class="mdi mdi-plus mr-2"></i> New Tag
                            </button>
            </template>
        </UsersHeader>

        <!-- Stats Cards -->
        <div class="mt-6 grid grid-cols-3 gap-6">
            <StatCard>
                <template #icon>
                    <i class="uil uil-tag text-2xl text-white"></i>
                </template>
                <template #title>Total Tags</template>
                <template #value>{{ rowData.length }}</template>
            </StatCard>

            <StatCard>
                <template #icon>
                    <i class="uil uil-layer-group text-2xl text-white"></i>
                </template>
                <template #title>Tag Types</template>
                <template #value>{{ tagTypes.length }}</template>
            </StatCard>
        </div>

        <!-- Main Content -->
        <div class="py-6">
            <div class="bg-echoes-background-gray shadow-sm rounded-lg border border-echoes-border-light">
                <!-- Search and Filters -->
                <div class="p-4">
                    <div class="flex flex-col sm:flex-row gap-4 items-center justify-between">
                        <div class="w-full sm:w-96">
                            <TextInput 
                                v-model="searchQuery" 
                                placeholder="Search tags..." 
                                class="w-full"
                            />
                        </div>
                        <div class="flex items-center gap-4">
                            <div class="w-full sm:w-48">
                                <SelectInput
                                    v-model="itemsPerPage"
                                    :options="itemsPerPageOptions"
                                    :hide-search="true"
                                    @update:modelValue="currentPage = 1"
                                />
                            </div>
                     
                        </div>
                    </div>
                </div>

                <!-- Table -->
                <div>
                    <div v-if="rowData.length === 0" class="h-[40vh]">
                        <div class="flex items-center justify-center h-full">
                            <div class="text-white/80 text-sm">No tags found</div>
                        </div>
                    </div>
                    <table v-else class="min-w-full divide-y divide-echoes-border-light">
                        <thead class="bg-echoes-border-light">
                            <tr>
                                <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                    Tag Details
                                </th>
                                <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                    Type
                                </th>
                                <th scope="col" class="px-6 py-4 text-center text-xs font-medium text-white uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody class="bg-echoes-background-gray divide-y divide-echoes-border-light">
                            <template v-for="tag in paginatedTags" :key="tag.id">
                                <tr class="bg-echoes-background-black/80 hover:bg-echoes-background-black/50 transition-colors duration-200">
                                    <td class="px-6 py-4">
                                        <div class="flex items-center">
                                            <div class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold"
                                                 :style="{ backgroundColor: tag.display_color, color: getContrastColor(tag.display_color) }">
                                                <i v-if="tag.display_icon" :class="[getIconClass(tag.display_icon), 'text-sm']"></i>
                                                {{ tag.name }}
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <span class="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-echoes-background-black border border-echoes-border-light text-white/80">
                                            {{ tag.type }}
                                        </span>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="flex items-center justify-center space-x-4">
                                            <button 
                                                @click="editTag(tag)"
                                                class="inline-flex items-center px-3 py-2 text-sm font-medium rounded-xl bg-echoes-background-black/60 text-white hover:bg-white/5 transition-colors duration-200 border border-echoes-border-light"
                                                title="Edit Tag"
                                            >
                                            <i class="mdi mdi-pencil mr-2"></i>
                                            Edit
                                            </button>
                                            <button 
                                                @click="deleteTag(tag)"
                                               class="inline-flex items-center px-3 py-2 text-sm font-medium rounded-xl bg-echoes-background-black/60 text-white hover:bg-white/5 transition-colors duration-200 border border-echoes-border-light"
                                                title="Delete Tag"
                                            >
                                            <i class="mdi mdi-trash-can-outline mr-2"></i>
                                            Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                    <!-- Pagination Footer -->
                    <div class="bg-echoes-background-gray border-t rounded-b-lg border-echoes-border-light px-6 py-4">
                        <div class="flex items-center justify-between">
                            <div class="flex items-center text-sm text-white/80">
                                <span>Showing</span>
                                <span class="font-medium mx-1">
                                    {{ ((currentPage - 1) * itemsPerPage) + 1 }}
                                </span>
                                <span>to</span>
                                <span class="font-medium mx-1">
                                    {{ Math.min(currentPage * itemsPerPage, filteredTags.length) }}
                                </span>
                                <span>of</span>
                                <span class="font-medium ml-1">{{ filteredTags.length }}</span>
                            </div>
                            <div class="flex items-center space-x-2">
                                <button
                                    @click="currentPage = Math.max(1, currentPage - 1)"
                                    :disabled="currentPage === 1"
                                    class="px-3 py-1 rounded-md text-sm font-medium text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>
                               
                                <div class="flex items-center space-x-1">
                                    <template v-for="(item, index) in visiblePages" :key="index">
                                        <template v-if="typeof item === 'number'">
                                            <button
                                                @click="currentPage = item"
                                                :class="[
                                                    'px-3 py-1 rounded-md text-sm font-medium',
                                                    currentPage === item
                                                        ? 'bg-echoes-primary text-white'
                                                        : 'text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80'
                                                ]"
                                            >
                                                {{ item }}
                                            </button>
                                        </template>
                                        <template v-else>
                                            <span class="px-3 py-1 text-sm text-white/60">...</span>
                                        </template>
                                    </template>
                                </div>
                                <button
                                    @click="currentPage = Math.min(totalPages, currentPage + 1)"
                                    :disabled="currentPage === totalPages"
                                    class="px-3 py-1 rounded-md text-sm font-medium text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Create/Edit Tag Modal -->
        <div v-if="showModal" class="fixed inset-0 z-50 overflow-y-auto">
            <div class="flex items-center justify-center min-h-screen px-4">
                <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity" @click="closeModal"></div>
                
                <div class="relative bg-echoes-background-gray border border-echoes-border-light rounded-xl max-w-md w-full">
                    <div class="flex justify-between items-center px-6 py-4 border-b border-echoes-border-light">
                        <div>
                            <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
                                {{ isEditing ? 'Edit Tag' : 'New Tag' }}
                            </h2>
                            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                {{ isEditing ? 'Update tag information' : 'Create a new tag' }}
                            </p>
                        </div>
                        <button 
                            @click="closeModal" 
                            class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                        >
                            <span class="sr-only">Close</span>
                            <svg class="p-2 w-10 h-10 rounded-lg text-white/80 hover:text-red-500/80 bg-transparent hover:bg-white/5 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <form @submit.prevent="handleSubmit" class="p-6 space-y-6">
                        <div class="flex gap-2 items-center">
                            <div class="flex flex-col gap-1 w-full">
                                <label for="tagName" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Tag Name
                                </label>
                                <TextInput v-model="newTag" placeholder="Enter tag name" />
                            </div>

                            <div class="flex flex-col gap-1">
                                <label for="tagIcon" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Tag Icon
                                </label>
                                <IconSelect v-model="selectedIcon" placeholder="Select icon" />
                            </div>
                        </div>

                        <div>
                            <div class="flex gap-2 items-center mb-1">
                                <label for="tagType" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Tag Type
                                </label>
                                <button 
                                    type="button"
                                    @click="isCustomType = !isCustomType"
                                    class="text-echoes-primary hover:text-echoes-primary/80"
                                    :title="isCustomType ? 'Switch to predefined types' : 'Add custom type'"
                                >
                                    <i class="mdi" :class="isCustomType ? 'mdi-format-list-bulleted' : 'mdi-plus'"></i>
                                </button>
                            </div>
                            <div v-if="isCustomType">
                                <TextInput id="tagType" v-model="newTagType" placeholder="Enter custom tag type" />
                            </div>
                            <div v-else>
                                <SelectInput id="tagType" :options="tagTypes" v-model="newTagType" placeholder="Select tag type" />
                            </div>
                        </div>

                        <div>
                            <label for="tagColor" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Tag Color
                            </label>
                            <div class="flex items-center gap-4">
                                <input
                                    type="color"
                                    id="tagColor"
                                    v-model="selectedColor"
                                    class="h-10 w-20 rounded-lg cursor-pointer border-none bg-transparent"
                                >
                                <div class="flex w-[96px]">
                                    <TextInput :autocomplete="'off'" id="tagColor" v-model="selectedColor" />
                                </div>
                            </div>
                        </div>

                        <div class="mt-4">
                            <label for="tagPreview" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Preview
                            </label>
                            <div id="tagPreview" class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium"
                                :style="{ backgroundColor: selectedColor, color: getContrastColor(selectedColor) }">
                                <i v-if="selectedIcon" :class="[getIconClass(selectedIcon), 'text-sm']"></i>
                                {{ newTag || 'Tag Preview' }}
                            </div>
                            <span class="ml-2 text-sm text-gray-500 dark:text-gray-400">
                                ({{ newTagType }})
                            </span>
                        </div>

                        <div class="flex justify-end gap-3 mt-6">
                            <button 
                                type="button"
                                @click="closeModal"
                                class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-400"
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit"
                                class="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-echoes-primary hover:bg-echoes-primary/90 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-echoes-primary"
                            >
                                {{ isEditing ? 'Save Changes' : 'Create Tag' }}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        <!-- Import Tags Modal -->
        <div v-if="showImportModal" class="fixed inset-0 z-50 overflow-y-auto">
            <div class="flex items-center justify-center min-h-screen px-4">
                <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity" @click="closeImportModal"></div>
                
                <div class="relative bg-echoes-background-gray border border-echoes-border-light rounded-xl max-w-md w-full">
                    <div class="flex justify-between items-center px-6 py-4 border-b border-echoes-border-light">
                        <div>
                            <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
                                Import Tags from CSV
                            </h2>
                            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                Upload a CSV file to import tags in bulk
                            </p>
                        </div>
                        <button 
                            @click="closeImportModal" 
                            class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                        >
                            <span class="sr-only">Close</span>
                            <svg class="p-2 w-10 h-10 rounded-lg text-white/80 hover:text-red-500/80 bg-transparent hover:bg-white/5 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <form @submit.prevent="handleImport" class="p-6 space-y-6" enctype="multipart/form-data">
                        <div>
                            <label for="csvFile" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                CSV File
                            </label>
                            <div class="flex items-center gap-4">
                                <input
                                    type="file"
                                    id="csvFile"
                                    ref="fileInput"
                                    accept=".csv"
                                    @change="handleFileSelect"
                                    class="hidden"
                                />
                                <button
                                    type="button"
                                    @click="selectFile"
                                    class="inline-flex items-center px-4 py-2 text-sm font-medium rounded-xl bg-echoes-background-black/60 text-white hover:bg-white/5 transition-colors duration-200 border border-echoes-border-light"
                                >
                                    <i class="mdi mdi-file-upload mr-2"></i>
                                    {{ selectedCsvFile ? selectedCsvFile.name : 'Choose CSV File' }}
                                </button>
                                <button
                                    v-if="selectedCsvFile"
                                    type="button"
                                    @click="clearFile"
                                    class="text-red-500 hover:text-red-600"
                                >
                                    <i class="mdi mdi-close"></i>
                                </button>
                            </div>
                            <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                Format: tag type name; tag type description; tag name; tag display color
                            </p>
                            <p v-if="csvError" class="mt-2 text-xs text-red-500">{{ csvError }}</p>
                        </div>

                        <div class="flex justify-end gap-3 mt-6">
                            <button 
                                type="button"
                                @click="closeImportModal"
                                class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-400"
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit"
                                :disabled="!selectedCsvFile || isImporting"
                                class="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-echoes-primary hover:bg-echoes-primary/90 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-echoes-primary disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <span v-if="isImporting">Importing...</span>
                                <span v-else>Import Tags</span>
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </main>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import axios from '@/plugins/axios';
import { useToast } from '@/composables/use-toast';
import IconSelect from '@/components/forms/inputs/IconSelect.vue';
import { useIconSelect } from '@/composables/useIconSelect.js';
import { useLayoutComponents } from '@/composables/layout.js';
import UsersHeader from '@/pages/admin/AdminHeader.vue';
import StatCard from '@/pages/admin/StatCard.vue';

const tagTypes = ref([]);
const showModal = ref(false);
const newTag = ref('');
const selectedColor = ref('#3B82F6');
const newTagType = ref('');
const selectedFilterType = ref('all');
const searchQuery = ref('');
const isEditing = ref(false);
const editingTagId = ref(null);
const isCustomType = ref(false);
const selectedIcon = ref('');
const showImportModal = ref(false);
const selectedCsvFile = ref(null);
const csvError = ref('');
const isImporting = ref(false);
const fileInput = ref(null);

// Pagination variables
const currentPage = ref(1);
const itemsPerPage = ref(10);
const itemsPerPageOptions = [
    { value: 10, label: '10' },
    { value: 25, label: '25' },
    { value: 50, label: '50' },
    { value: 100, label: '100' },
];

const getIconClass = (icon) => {
    if (!icon) return '';
    
    if (icon.includes(' ')) {
        return icon;
    }
    
    const iconObj = icons.value.find(i => i.name === icon);
    return iconObj ? iconObj.class : '';
};

const rowData = ref([]);

const { addToast } = useToast();
const { SelectInput, TextInput } = useLayoutComponents();
const { getAllIcons } = useIconSelect();
const icons = ref(getAllIcons());

// Computed properties for filtering and pagination
const filteredTags = computed(() => {
    let filtered = rowData.value;
    if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        filtered = rowData.value.filter(tag => 
            tag.name.toLowerCase().includes(query) ||
            tag.type.toLowerCase().includes(query)
        );
    }
    return filtered;
});

const totalPages = computed(() => {
    return Math.ceil(filteredTags.value.length / itemsPerPage.value);
});

const paginatedTags = computed(() => {
    const start = (currentPage.value - 1) * itemsPerPage.value;
    const end = start + itemsPerPage.value;
    return filteredTags.value.slice(start, end);
});

const visiblePages = computed(() => {
    const total = totalPages.value;
    const current = currentPage.value;
    const delta = 2;
    
    if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i + 1);
    }
    
    const range = [];
    const rangeWithDots = [];
    
    range.push(1);
    
    for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) {
        range.push(i);
    }
    
    if (total > 1) {
        range.push(total);
    }
    
    for (let i = 0; i < range.length; i++) {
        if (i > 0) {
            const gap = range[i] - range[i - 1];
            if (gap === 2) {
                rangeWithDots.push(range[i - 1] + 1);
            } else if (gap > 2) {
                rangeWithDots.push('...');
            }
        }
        rangeWithDots.push(range[i]);
    }
    
    return rangeWithDots;
});


onMounted(async () => {
    await fetchTags();
    await fetchTagTypes();
});

const fetchTags = async () => {
    try {
        const response = await axios.get('/api/tags');
        if(response.status === 200) {
            selectedFilterType.value = 'all';
            rowData.value = response.data.map(tag => ({
                id: tag.id,
                name: tag.name,
                display_color: tag.display_color,
                display_icon: tag.display_icon,
                type: tag?.tag_type?.type || '-',
                type_tooltip: tag?.tag_type,
                tag_type_id: tag?.tag_type_id,
                original_type: tag?.tag_type?.type
            }));
        } else {
            console.error('Error fetching tags:', response.data);
        }
    } catch (error) {
        console.error('Error fetching tags:', error);
    }
};

const fetchTagTypes = async () => {
    try {
        const response = await axios.get('/api/tag_types');
        if(response.status === 200) {
            tagTypes.value = response.data.map(tagTypes => ({
                label: tagTypes.type,
                value: tagTypes.type
            }));
        } else {
            console.error('Error fetching tags:', response.data);
        }
    } catch (error) {
        console.error('Error fetching tags:', error);
    }
};

const getContrastColor = (hexColor) => {
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    return yiq >= 128 ? '#000000' : '#FFFFFF';
};

const createTag = () => {
    isEditing.value = false;
    newTag.value = '';
    selectedColor.value = '#3B82F6';
    newTagType.value = '';
    selectedIcon.value = '';
    showModal.value = true;
};

const editTag = (tag) => {
    isEditing.value = true;
    editingTagId.value = tag.id;
    newTag.value = tag.name;
    selectedColor.value = tag.display_color;
    newTagType.value = tag.original_type;
    selectedIcon.value = tag.display_icon || '';
    showModal.value = true;
};

const closeModal = () => {
    showModal.value = false;
    newTag.value = '';
    selectedColor.value = '#3B82F6';
    newTagType.value = '';
    selectedIcon.value = '';
    isEditing.value = false;
    editingTagId.value = null;
    isCustomType.value = false;
};

const deleteTag = async (tag) => {
    try {
        if(confirm('Are you sure you want to delete this tag?') && tag?.id) {
            const response = await axios.post(`/api/tags/${tag.id}/delete`);
            if(response.status === 200 || response.status === 201) {
                await fetchTags();
                addToast('Tag Deleted','success');
            } else {
                addToast(response.data.message || 'An error occurred','error');
            }
        }
    } catch (error) {
        addToast(error.response?.data?.message || 'An error occurred','error');
        console.error('Error deleting tag:', error);
    }
};

const handleSubmit = async () => {
    const url = isEditing.value ? `/api/tags/${editingTagId.value}` : '/api/tags';
    const payload = {
        name: newTag.value,
        display_color: selectedColor.value,
        display_icon: selectedIcon.value,
        type: newTagType.value,
    };

    try {
        const response = await axios.post(url, payload);
        if (response.status === 201) {
            await fetchTags();
            closeModal();
        } else {
            console.error('Error creating/updating tag:', response.data);
        }
    } catch (error) {
        console.error('Error creating/updating tag:', error);
    }
    closeModal();
};

const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) {
        selectedCsvFile.value = null;
        csvError.value = '';
        return;
    }

    // Validate file extension
    if (!file.name.toLowerCase().endsWith('.csv')) {
        csvError.value = 'Please select a CSV file';
        selectedCsvFile.value = null;
        return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
        csvError.value = 'File size must be less than 10MB';
        selectedCsvFile.value = null;
        return;
    }

    // Validate MIME type
    const validMimeTypes = ['text/csv', 'application/csv', 'text/plain', 'application/vnd.ms-excel'];
    if (file.type && !validMimeTypes.includes(file.type.toLowerCase())) {
        csvError.value = 'Invalid file type. Please select a CSV file';
        selectedCsvFile.value = null;
        return;
    }

    csvError.value = '';
    selectedCsvFile.value = file;
};

const selectFile = () => {
    if (fileInput.value) {
        fileInput.value.click();
    }
};

const clearFile = () => {
    selectedCsvFile.value = null;
    csvError.value = '';
    if (fileInput.value) {
        fileInput.value.value = '';
    }
};

const closeImportModal = () => {
    showImportModal.value = false;
    clearFile();
};

const getUploadHeaders = () => {
    const token = localStorage.getItem('authToken');
    const headers = {
        'Accept': 'application/json'
    };
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
};

const validateCsvFormat = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const content = e.target.result;
            const lines = content.split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0);
            
            if (lines.length === 0) {
                reject('CSV file is empty');
                return;
            }

            // Check for semicolon separator
            if (!content.includes(';')) {
                reject('CSV file must use semicolon (;) as separator');
                return;
            }

            // Validate each row has 4 columns
            for (let i = 0; i < lines.length; i++) {
                const parts = lines[i].split(';').map(p => p.trim());
                if (parts.length !== 4 || !parts[0] || !parts[1] || !parts[2] || !parts[3]) {
                    reject(`Invalid row format at line ${i + 1}. Expected 4 columns: tag type name; tag type description; tag name; tag display color`);
                    return;
                }
            }

            resolve(true);
        };
        reader.onerror = () => reject('Error reading file');
        reader.readAsText(file);
    });
};

const handleImport = async () => {
    if (!selectedCsvFile.value) {
        csvError.value = 'Please select a CSV file';
        return;
    }

    isImporting.value = true;
    csvError.value = '';

    try {
        // Validate CSV format
        await validateCsvFormat(selectedCsvFile.value);
    } catch (error) {
        csvError.value = error.message || 'Invalid CSV format';
        isImporting.value = false;
        return;
    }

    try {
        const formData = new FormData();
        formData.append('file', selectedCsvFile.value);

        // Use fetch instead of axios to avoid Content-Type header override from interceptor
        // The browser will automatically set the correct Content-Type with boundary for FormData
        // This follows the same pattern used in evalService.js and api.js for file uploads
        const baseURL = axios.defaults.baseURL || 'http://localhost:3000';
        const headers = getUploadHeaders();
        
        const response = await fetch(`${baseURL}/api/tags/import`, {
            method: 'POST',
            body: formData,
            credentials: 'include',
            headers: headers
        });

        let responseData;
        try {
            responseData = await response.json();
        } catch (e) {
            responseData = { message: 'An error occurred during import' };
        }

        if (response.status === 202) {
            addToast(responseData.message || 'CSV file validated successfully and will be processed', 'success');
            closeImportModal();
            // Refresh tags after a short delay to allow processing
            setTimeout(() => {
                fetchTags();
            }, 2000);
        } else {
            const errorMessage = responseData.message || 'An error occurred during import';
            csvError.value = errorMessage;
            addToast(errorMessage, 'error');
        }
    } catch (error) {
        const errorMessage = error.message || 'An error occurred during import';
        csvError.value = errorMessage;
        addToast(errorMessage, 'error');
    } finally {
        isImporting.value = false;
    }
};
</script>

<style scoped lang="postcss">
/* Custom Scrollbar */
::-webkit-scrollbar {
    @apply w-2;
}

::-webkit-scrollbar-track {
    @apply bg-echoes-background-black rounded-full;
}

::-webkit-scrollbar-thumb {
    @apply bg-echoes-background-gray rounded-full transition-colors duration-200;
}

::-webkit-scrollbar-thumb:hover {
    @apply bg-echoes-background-gray/80;
}

/* Animations */
@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}

@keyframes slideIn {
    from { transform: translateY(-10px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
}

/* Stats Cards Hover Effect */
.bg-white.dark\:bg-slate-700 {
    @apply transition-all duration-200 hover:shadow-lg hover:scale-[1.02];
}
</style>